/**
 * ControlCheckbox — the Report Center's DRAWN checkbox.
 *
 * spec 117 / Himaad B4+B5. Inside one Display View popover there were two
 * different checkbox implementations, and that is the whole of the defect
 * Himaad and Jordan described ("the view options checkbox below looks lighter
 * color" / "this one doesn't have the hover"):
 *
 *   - the COLUMN rows were a hand-drawn 14px box (DataGrid.jsx:1165,
 *     ReportGroupedGrid.jsx:1481, ReportExport.jsx:306), and
 *   - the VIEW OPTIONS rows below them were NATIVE <input type="checkbox">
 *     wearing `rounded border-<colour>` — which paints NOTHING, because
 *     Tailwind v4's preflight sets `border-width: 0` on `*` and no bare
 *     `border` width utility sat beside the colour. So those classes were
 *     inert and the browser drew its own default control.
 *
 * A border-colour swap would therefore have been ZERO pixels. The fix is to
 * make the second list use the first list's control, which is what this is.
 *
 * WHY A REAL <input> AND NOT role="checkbox":
 *   1. Keyboard operability comes free and correct — Space toggles, Tab
 *      reaches it, the checked state is the element's own (WCAG 2.1.1). An
 *      aria-label assertion never proves that; a `role="checkbox"` <button>
 *      would have needed hand-written key handling.
 *   2. It does not change the DOM shape the suites count.
 *      ReportFilterPanel.test.jsx:1036 asserts
 *      `panel.querySelectorAll('input[type="checkbox"]').length > 0` and :183
 *      counts exactly 2 — both still hold, because the input is still here.
 *      It is transparent, not removed, and not `display:none`, so it keeps its
 *      accessibility-tree role and `getByRole('checkbox')` still finds it.
 *
 * The input is laid over the drawn box at exactly its size, so a pointer press
 * anywhere on the mark hits the real control. Every call site additionally
 * wraps this in a <label>, which is what gives the control both its accessible
 * name and a target far larger than SC 2.5.8's 24x24 floor.
 *
 * COLOUR — and a deliberate deviation from the mock, measured against EVERY
 * ground this component actually renders on rather than only white. An
 * unchecked box is white-on-white-ish, so its 1px border is the ONLY thing
 * drawing the control, and SC 1.4.11 wants 3.0:1 for it.
 *
 * The thirteen call sites sit on three different grounds. Recomputed from the
 * hexes in src/index.css's @theme block (sRGB relative luminance, then
 * (L1+0.05)/(L2+0.05)):
 *
 *                     #ffffff   #f9fafb   #f3f4f6
 *   mock's #9ca3af      2.539     2.429     2.307   FAILS everywhere
 *   rc-control #8b95a5  3.026     2.896     2.750   passes on WHITE ONLY
 *   rc-seam    #6b7280  4.834     4.626     4.393   passes with headroom
 *
 * where #f9fafb is Layout.jsx:82's page ground (CsvReportGrid's ghost-column
 * opt-in has no card of its own) and every converted row's hover band, and
 * #f3f4f6 is the flat grid's filtered-totals coherence bar (bg-gray-100).
 *
 * So the unchecked boundary is --color-rc-seam, NOT --color-rc-control. That is
 * the rule src/index.css:85 already states in @theme — "Never reuse a *-line
 * token, or --color-rc-control, in that position; use --color-rc-seam" — for a
 * boundary whose other side is not page white. Nine of the thirteen sites are
 * such a boundary at rest or on hover, so rc-control was the wrong token here
 * even though it is what the column rows above these wear.
 *
 * KNOWN, DISCLOSED CONSEQUENCE — narrowed 2026-09-09. The column rows in the
 * grouped grid's own popover (ReportGroupedGrid.jsx:1503) now take rc-seam too,
 * so on the 86 grouped reports the Display View popover is ONE colour again and
 * the 2.896:1 hover-state failure is gone from it. Two byte-identical copies of
 * this class string remain on `border-rc-control`, both outside this stream's
 * partition: DataGrid.jsx:1165 (the flat grid's column rows) and
 * ReportExport.jsx:306. So on the 33 FLAT reports the popover still shows its
 * column rows one step lighter than the View options rows below them.
 *
 * That is a deliberate choice between two inconsistencies, made on which one a
 * user can actually see: the flat and grouped popovers are never on screen
 * together, whereas the column rows and the View options rows are eight pixels
 * apart in the SAME list — which is literally Himaad's B4 complaint. Holding
 * rc-seam back to keep the two grids byte-identical would have kept a measured
 * SC 1.4.11 failure on all 140 reports to avoid a difference nobody can
 * perceive. spec 117 §5 G1 carries the two-line follow-up.
 *
 * Checked is --color-rc-chip-text #1d4ed8 with a white tick: 6.702 / 6.413 /
 * 6.090 across the same three grounds.
 *
 * PRINT. The wrapper is `print:hidden`. src/styles/print-report.css (imported
 * globally at src/main.jsx:10) hides `input`, `select`, `textarea` and `button`
 * in @media print, so before this component existed every one of these
 * checkboxes was simply absent from paper — and the three pre-existing drawn
 * boxes escape it only because each lives inside a <button
 * role="menuitemcheckbox">, which that stylesheet hides. These thirteen sit
 * inside a <label>, which it does not, and two of them print: both grids'
 * filtered-totals coherence bars carry no [data-print-hide]. Left visible, a
 * CHECKED box would print as an EMPTY one under Chrome's default "Background
 * graphics: off" — the `bg-rc-chip-text` fill is suppressed and the tick is
 * `text-white` — i.e. a printed financial artifact whose box contradicts the
 * sentence beside it ("Totals reflect the N filtered rows shown"). `print:hidden`
 * reproduces the pre-change paper output exactly at all thirteen sites.
 * Considered and rejected: `print:text-black` to keep a real ticked box on paper.
 * It is probably right and it is NOT verifiable here — print-report.css's own
 * header records that no unit test, neither e2e audit and no screenshot in this
 * repo evaluates @media print, so the only change worth making blind is the one
 * whose output is already known.
 *
 * Anything not named below is forwarded to the input, so a call site keeps its
 * own `data-pendo`, `disabled`, `aria-label` and friends.
 *
 * ## `hit24` — the SOLE survivor's one added prop (spec 121 gap 3)
 *
 * `GridCheckbox`, a byte-identical box and tick exported from DataGrid.jsx by
 * spec 116 one week after this component landed, has been DELETED and its three
 * call sites migrated here. This component is the one that survives, and the
 * deciding reason is an import cycle rather than taste: DataGrid.jsx:29 imports
 * ColumnValuePicker, which is a ControlCheckbox consumer, so migrating the other
 * way would have made DataGrid → ColumnValuePicker → DataGrid. Four of the seven
 * consumer files (ReportGroupedGrid, ReportFilterPanel, ColumnValuePicker,
 * CategorySelect) do not import DataGrid at all and would have gained a
 * 2000-line dependency for a 14px box.
 *
 * The one thing GridCheckbox had that this did not is a **24x24 hit area of its
 * own**: its input was `h-6 w-6` centred on the 14px visual, because two of its
 * three sites (the row-selection header and cell) are NOT wrapped in a <label>
 * and carry only an `aria-label`, so there was no larger target to inherit. All
 * thirteen ControlCheckbox sites ARE label-wrapped, so 14x14 is fine for them —
 * the label supplies both the name and a target well over SC 2.5.8's floor.
 * A bare swap at an unwrapped site would therefore have been a silent 24 → 14
 * regression, invisible because the drawn box looks identical at both hit sizes.
 * `hit24` is that overlay, opt-in: pass it wherever there is no wrapping <label>.
 *
 * 24px fits its host at both migrated sites: the selection column is 40px wide
 * with px-4, so the overlay spans x = 11..35 of the cell and never reaches a
 * neighbour — which matters, because the row-selection cell stops click
 * propagation and an overhang into the next cell would silently turn a row-open
 * click into a selection toggle.
 *
 * PRINT, for the migrated sites specifically: this wrapper's `print:hidden` now
 * applies to row selection too, which RESTORES the pre-spec-116 paper output
 * exactly. print-report.css hides `input`, so the native checkbox those sites
 * used before spec 116 was already absent from paper; GridCheckbox's drawn <span>
 * was not, and a CHECKED one printed as an EMPTY box under Chrome's default
 * "Background graphics: off" (the bg-rc-chip-text fill is suppressed and the tick
 * is text-white) — on a printed financial artifact. Neither unit tests nor either
 * e2e audit evaluate @media print in this repo, so the change worth making blind
 * is the one whose output is already known.
 */
const ControlCheckbox = ({ checked, onChange, className = '', hit24 = false, ...inputProps }) => (
  <span className={`relative inline-flex h-3.5 w-3.5 flex-none print:hidden ${className}`}>
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      /* `opacity-0`, NOT `sr-only` and NOT `hidden`: it has to keep a real
         box so the press lands on the input itself, and it has to stay
         in the accessibility tree. `appearance-none` stops the native control
         painting through the transparency on platforms that ignore opacity on
         form controls.
         `hit24` swaps the 14x14 box for a 24x24 one centred on the visual, for
         call sites with no wrapping <label> to inherit a target from. */
      className={`peer absolute m-0 cursor-pointer appearance-none opacity-0 disabled:cursor-not-allowed ${hit24 ? 'left-1/2 top-1/2 h-6 w-6 -translate-x-1/2 -translate-y-1/2' : 'inset-0 h-full w-full'}`}
      {...inputProps}
    />
    <span
      aria-hidden="true"
      /* Focus is drawn on the BOX, driven off the sibling input's own
         :focus-visible — the same 2px --color-rc-chip-text outline at 1px
         offset that .btn-rc / .select-rc / .input-rc use. The input itself is
         transparent, so the browser's own ring would land on something
         invisible; this puts it on the mark (SC 2.4.7).

         NOT "one focus treatment across the panel", corrected 2026-09-09. An
         earlier version of this note claimed that. It is false: src/index.css
         carries an UNLAYERED global `:focus-visible { outline: 2px solid
         var(--color-cb-navy); outline-offset: 2px }` (:199), which a
         @layer utilities class cannot suppress — so the column rows' own
         `focus-visible:outline-none` does nothing and they wear a 2px #09203d
         ring at 2px offset. Tabbing from the last column row into a View
         options row therefore changes the ring from navy/2px-offset to
         chip-text/1px-offset mid-list. Both are visible and both clear the
         floor, so neither is a WCAG failure and unifying them would mean an
         index.css rule, which is another stream's file this batch. Stated
         accurately so nobody audits against a promise that was never kept. */
      className={`grid h-3.5 w-3.5 place-items-center rounded-[3px] border peer-focus-visible:outline-2 peer-focus-visible:outline-offset-1 peer-focus-visible:outline-rc-chip-text peer-disabled:opacity-50 ${checked ? 'border-rc-chip-text bg-rc-chip-text text-white' : 'border-rc-seam bg-white'}`}
    >
      {checked && (
        <svg viewBox="0 0 10 8" className="h-2 w-2.5" fill="none" stroke="currentColor" strokeWidth={2.2}>
          <path d="M1 4l2.5 2.5L9 1" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </span>
  </span>
)

/**
 * TOGGLE_ROW — the row treatment that pairs with this control (spec 117 B5), lifted
 * here 2026-09-09 (spec 121 gap 2) from the two literal copies in
 * ReportDataGrid.jsx and ReportGroupedGrid.jsx.
 *
 * It moved because a THIRD site needs it: ARReport.jsx contributes two toggles into
 * the same popover list as `pageDisplayExtras`, and those rows have to match the
 * rows they sit eight pixels from — that mismatch (`gap-1.5` and no hover band
 * against `gap-2.5` and one) is half of Himaad's B4/B5 complaint, reproduced by a
 * file partition. A third hand-copy of the string is how the grids diverged in the
 * first place, so there is one.
 *
 * `-mx-3.5 px-3.5` is load-bearing, not decoration: the popover's own slot wrapper
 * is `px-3.5`, so without the negative margin the hover/focus band stops 14px short
 * of the panel edge on both sides.
 *
 * The band is NOT the focus indicator — #f9fafb on the white panel is 1.024:1,
 * imperceptible. What indicates focus is the box's own 2px --color-rc-chip-text
 * outline above; the band is row-level parity with `hover`, nothing more.
 */
/*
 * TOGGLE_ROW_BAND — the band, the full-bleed padding and the focus parity, WITHOUT
 * the checkbox row's `gap-2.5` or `cursor-pointer`.
 *
 * Added 2026-09-09 for a row that is not a checkbox row. ARReport contributes THREE
 * controls into this list, not two: `Hide zero balances`, `Hide inactive/closed
 * matters`, and a `Practice area` <select>. Banding only the two checkbox rows left
 * the third bandless — so on Aged A/R (id 21) two of three rows highlighted
 * together and the third did not, which is a NEW mismatch on the exact report gap 2
 * existed to fix. Before the conversion all three matched each other (all inert,
 * all `gap-1.5`), so half-converting made that list worse, not better.
 *
 * It cannot just take TOGGLE_ROW: `gap-2.5` is tuned to a 14px box beside its own
 * label, and it is too wide between the words "Practice area" and the select they
 * label. And `cursor-pointer` would be a lie over a <select>, which owns its own
 * cursor. So the band composes and the row keeps `gap-1.5`.
 */
export const TOGGLE_ROW_BAND = '-mx-3.5 px-3.5 py-1 hover:bg-gray-50 has-[:focus-visible]:bg-gray-50'

export const TOGGLE_ROW = `flex items-center gap-2.5 cursor-pointer ${TOGGLE_ROW_BAND}`

export default ControlCheckbox
